// =====================================================================
// SERVICE: logger.service.ts
// FUNCTIONAL OVERVIEW:
//   Centralized logger for v2 features. Wraps the browser console so
//   feature code never calls console.* directly. Debug output is
//   suppressed outside dev mode. Callers must never pass tokens, answers
//   or other sensitive payloads in the context object.
// DEPENDENCIES: none
// =====================================================================
import { Injectable, isDevMode } from '@angular/core';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  source: string;
  action: string;
  [key: string]: string | number | boolean | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Writes a debug entry (dev mode only).
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  debug(message: string, context: LogContext): void {
    if (isDevMode()) {
      this.write('debug', message, context);
    }
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Writes an informational entry.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  info(message: string, context: LogContext): void {
    this.write('info', message, context);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Writes a warning entry.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  warn(message: string, context: LogContext): void {
    this.write('warn', message, context);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Writes an error entry. Only the error name/message is kept, never
  //   the full object, so response bodies and stacks are not leaked.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  error(message: string, context: LogContext, error?: unknown): void {
    const errorInfo = error instanceof Error ? `${error.name}: ${error.message}` : undefined;
    this.write('error', message, errorInfo ? { ...context, error: errorInfo } : context);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Single console sink; replace here to route logs to monitoring.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5936] 2026-10-22 00:00 | Vikas Kumar | Initial implementation.
  // -----------------------------------------------------------------
  private write(level: LogLevel, message: string, context: LogContext): void {
    const line = `[${level.toUpperCase()}] [${context.source}] ${context.action}: ${message}`;
    // eslint-disable-next-line no-console
    console[level](line, context);
  }
}
